import { animate } from "motion/react"
import type { RefObject } from "react"
import { useEffect, useLayoutEffect, useRef } from "react"

import { springMediaZ } from "./motion"
import type { JacketPhase } from "./use-jacket-feed"

/**
 * The §7 travel: the disc-fitted media-box FLIPs between its CSS-owned rest
 * (seated behind the jacket's edge — .jacket-media-box) and the measured
 * media rect on the open stage.
 *
 * OPEN — the record slides out of the sleeve and lands on the platter. The
 * box's left/top/width/height animate in px (iframe law: never a transform),
 * and one normalized progress drives all four so the wrapper is true-aspect
 * at every frame (an iframe can't object-cover; a wrong-aspect wrapper
 * stretches the video). The SAME progress drives the clip circle's radius —
 * an iris from the disc to the full frame (not a third movement: it rides
 * springMediaZ). With the disc seated mostly behind the jacket, cutting the
 * clip at flight start would pop the box's hidden corners into view where
 * the crescent was; the iris opens the record into the picture instead.
 * (irisRef/coverR belong to the open travel only.)
 *
 * CLOSE — two honest beats. Beat 1 ("closing"): the record lifts off (fades
 * out) and reparks HIDDEN while the empty sleeve crossfades in composed. Beat
 * 2 ("seating"): the record — media-box AND its vinyl texture as one disc
 * group — slides in from off-screen-right into the die-cut on springMediaZ,
 * constant-radius (a rigid record, no iris), and seats; onCloseSettled →
 * "ambient". Article/audio have no media rect, so they settle at Beat 1.
 *
 * Everything here is refs + animate() in a layout effect — the established
 * seat-effect pattern; no setState anywhere.
 */

interface Rect {
  left: number
  top: number
  width: number
  height: number
}

const lerp = (a: number, b: number, p: number) => a + (b - a) * p
const lerpRect = (a: Rect, b: Rect, p: number): Rect => ({
  left: lerp(a.left, b.left, p),
  top: lerp(a.top, b.top, p),
  width: lerp(a.width, b.width, p),
  height: lerp(a.height, b.height, p),
})

/** The largest aspect-true rect centered inside the stage's media area. */
function fitRect(area: DOMRect, root: DOMRect, aspect: number): Rect {
  const width = Math.min(area.width, area.height * aspect)
  const height = width / aspect
  return {
    left: area.left - root.left + (area.width - width) / 2,
    top: area.top - root.top + (area.height - height) / 2,
    width,
    height,
  }
}

export function useMediaTravel({
  phase,
  reduce,
  travels,
  rootRef,
  mediaBoxRef,
  holeProbeRef,
  portholeTextureRef,
  stageRef,
  stageMediaRef,
  onLanded,
  onCloseSettled,
}: {
  phase: JacketPhase
  reduce: boolean
  /** video/photo — the kinds whose media rides the box to the stage. */
  travels: boolean
  rootRef: RefObject<HTMLDivElement | null>
  mediaBoxRef: RefObject<HTMLDivElement | null>
  holeProbeRef: RefObject<HTMLDivElement | null>
  /** The vinyl-texture wrapper — rides the box in lockstep on Beat 2's slide
   * via translateX (it carries no iframe, so a transform is legal). */
  portholeTextureRef: RefObject<HTMLDivElement | null>
  stageRef: RefObject<HTMLDivElement | null>
  stageMediaRef: RefObject<HTMLDivElement | null>
  /** The record landed on the platter — the bridged unmute beat. */
  onLanded: () => void
  /** The record is home in the die-cut — the sleeve may re-enter. */
  onCloseSettled: () => void
}) {
  const controlsRef = useRef<ReturnType<typeof animate> | null>(null)
  const openedRef = useRef(false)
  const inFlightRef = useRef(false)
  // Set at Beat 1 from openedRef: was the record actually out on the stage
  // (so there is something to slide back in on Beat 2)? An Escape mid-open
  // before the record left leaves this false — Beat 2 then just settles.
  const reparkedRef = useRef(false)
  // The iris radius mid-flight — an Escape mid-open reverses from here
  // instead of guessing from the box's current size.
  const irisRef = useRef<number | null>(null)
  // Callback refs so the layout effect's deps stay [phase, reduce, travels].
  const onLandedRef = useRef(onLanded)
  const onCloseSettledRef = useRef(onCloseSettled)
  useEffect(() => {
    onLandedRef.current = onLanded
    onCloseSettledRef.current = onCloseSettled
  })

  useLayoutEffect(() => {
    if (!travels) return
    const box = mediaBoxRef.current
    const root = rootRef.current
    if (!box || !root) return

    const aspect = () =>
      parseFloat(getComputedStyle(root).getPropertyValue("--media-aspect")) ||
      16 / 9
    const setBox = (rect: Rect) => {
      box.style.left = `${rect.left}px`
      box.style.top = `${rect.top}px`
      box.style.width = `${rect.width}px`
      box.style.height = `${rect.height}px`
    }
    const clearBox = () => {
      // removeProperty (never a style-attribute nuke): CSS resolves back to
      // the same formula the travel landed on, and the circle clip returns —
      // that reseat is the choreography's one sanctioned instant cut.
      for (const prop of [
        "left",
        "top",
        "width",
        "height",
        "clip-path",
        "opacity",
      ])
        box.style.removeProperty(prop)
    }
    const boxRect = (): Rect => {
      const rootB = root.getBoundingClientRect()
      const b = box.getBoundingClientRect()
      return {
        left: b.left - rootB.left,
        top: b.top - rootB.top,
        width: b.width,
        height: b.height,
      }
    }
    /** Where CSS will put the box once inline styles clear (rest = the hole). */
    const restRect = (): Rect => {
      const rootB = root.getBoundingClientRect()
      const probeB = holeProbeRef.current?.getBoundingClientRect()
      // Registered @property --hole-r resolves to used px (v4 §8).
      const r =
        parseFloat(getComputedStyle(root).getPropertyValue("--hole-r")) || 88
      const height = r * 2 + 8
      const width = height * aspect()
      const cx = probeB ? probeB.left - rootB.left : rootB.width
      const cy = probeB ? probeB.top - rootB.top : rootB.height
      return { left: cx - width / 2, top: cy - height / 2, width, height }
    }
    const stageTarget = (): Rect | null => {
      const area = stageMediaRef.current?.getBoundingClientRect()
      if (!area) return null
      return fitRect(area, root.getBoundingClientRect(), aspect())
    }
    /** Resolved disc radius (registered @property --hole-r → used px). */
    const holeR = () =>
      parseFloat(getComputedStyle(root).getPropertyValue("--hole-r")) || 88
    /** The radius at which a centered circle stops clipping a rect. */
    const coverR = (rect: Rect) => Math.hypot(rect.width, rect.height) / 2
    /** Beat 2 park distance: shift the disc so its LEFT edge sits at the
     * root's right edge (+ pad to fully clear), so the record slides in from
     * off-screen-right. Computed from the hole probe and the registered
     * @property --hole-r (never a literal); the same value drives the
     * media-box left AND the texture translateX so both ride in lockstep. */
    const offRight = (): number => {
      const rootB = root.getBoundingClientRect()
      const probeB = holeProbeRef.current?.getBoundingClientRect()
      const holeCx = probeB ? probeB.left - rootB.left : rootB.width
      const pad = 24
      return rootB.width - (holeCx - holeR()) + pad
    }
    const travel = (
      from: Rect,
      to: Rect,
      rFrom: number,
      rTo: number,
      onComplete: () => void,
    ) => {
      inFlightRef.current = true
      controlsRef.current = animate(0, 1, {
        ...springMediaZ,
        onUpdate: (p) => {
          setBox(lerpRect(from, to, p))
          const r = lerp(rFrom, rTo, p)
          irisRef.current = r
          box.style.clipPath = `circle(${r}px at 50% 50%)`
        },
        onComplete: () => {
          inFlightRef.current = false
          irisRef.current = null
          onComplete()
        },
      })
    }

    if (phase === "opening" || phase === "active" || phase === "upnext") {
      // The opening→active re-run (jacketDeparted mid-spring) and
      // active⇄upnext must NOT restart a running or landed travel.
      if (openedRef.current) return
      const to = stageTarget()
      if (!to) return
      openedRef.current = true
      if (reduce) {
        // Reduced motion: no travel frames exist — pin the box at the landed
        // rect and 200ms-crossfade it in; the needle drops at the fade's end.
        setBox(to)
        box.style.clipPath = "none"
        box.style.opacity = "0"
        inFlightRef.current = true
        controlsRef.current = animate(0, 1, {
          duration: 0.2,
          onUpdate: (p) => {
            box.style.opacity = String(p)
          },
          onComplete: () => {
            box.style.removeProperty("opacity")
            inFlightRef.current = false
            onLandedRef.current()
          },
        })
      } else {
        const from = boxRect()
        setBox(from) // pin px ≡ the CSS calc's used values: no visible delta
        // Iris out from the disc (pinning at holeR ≡ the CSS circle: no
        // visible delta either); at landing the clip is spent — hand the
        // box to the resize observer unclipped.
        travel(from, to, holeR(), coverR(to), () => {
          box.style.clipPath = "none"
          onLandedRef.current()
        })
      }
    } else if (phase === "closing") {
      // Beat 1: the record lifts off (fades out) and reparks hidden while the
      // empty sleeve crossfades in over it. reparkedRef captures whether the
      // record was actually out (so Beat 2 has something to slide in). The
      // phase is driven forward by sleeveComposed — do NOT settle from here.
      // Kill an in-flight open spring without firing its completion (no stray
      // unmute).
      controlsRef.current?.stop()
      reparkedRef.current = openedRef.current
      openedRef.current = false
      if (reduce) {
        clearBox()
        box.style.opacity = "0"
      } else {
        // Fade out over 200ms, in sync with the stage's 200ms exit, then park
        // hidden at CSS rest (clearBox hands geometry back to CSS; opacity 0
        // keeps it invisible until Beat 2 sets it visible while off-screen).
        inFlightRef.current = true
        controlsRef.current = animate(1, 0, {
          duration: 0.2,
          onUpdate: (p) => {
            box.style.opacity = String(p)
          },
          onComplete: () => {
            clearBox()
            box.style.opacity = "0"
            inFlightRef.current = false
          },
        })
      }
    } else if (phase === "seating") {
      // Beat 2 (travel kinds): the record — media-box AND vinyl texture as one
      // disc group — slides in from off-screen-right into the die-cut and
      // seats. One springMediaZ drives the box's inline `left` (iframe law: px,
      // never a transform) and the texture's translateX in lockstep.
      controlsRef.current?.stop()
      const tex = portholeTextureRef.current
      if (!reparkedRef.current) {
        // Nothing to insert (e.g. Escape mid-open before the record left the
        // sleeve): return the box to CSS rest, clear the texture, settle.
        clearBox()
        tex?.style.removeProperty("transform")
        onCloseSettledRef.current()
        return
      }
      const from = restRect()
      const offset = offRight()
      // A rigid record: constant-radius clip (no iris), constant w/h — a
      // single-axis slide.
      box.style.clipPath = `circle(${holeR()}px at 50% 50%)`
      if (reduce) {
        // Reduced motion: no slide — seat at rest and 200ms-crossfade in (the
        // record fades back rather than pops).
        setBox(from)
        inFlightRef.current = true
        controlsRef.current = animate(0, 1, {
          duration: 0.2,
          onUpdate: (p) => {
            box.style.opacity = String(p)
          },
          onComplete: () => {
            clearBox()
            tex?.style.removeProperty("transform")
            inFlightRef.current = false
            onCloseSettledRef.current()
          },
        })
      } else {
        const parked = { ...from, left: from.left + offset }
        setBox(parked)
        // Made visible while still off-screen-right — the teleport is never
        // seen (Beat 1 left it opacity 0).
        box.style.opacity = "1"
        if (tex) tex.style.transform = `translateX(${offset}px)`
        inFlightRef.current = true
        controlsRef.current = animate(0, 1, {
          ...springMediaZ,
          onUpdate: (p) => {
            box.style.left = `${lerp(parked.left, from.left, p)}px`
            if (tex) tex.style.transform = `translateX(${offset * (1 - p)}px)`
          },
          onComplete: () => {
            clearBox()
            tex?.style.removeProperty("transform")
            inFlightRef.current = false
            onCloseSettledRef.current()
          },
        })
      }
      reparkedRef.current = false
    } else {
      // ambient — an Embla select mid-travel lands here without passing
      // through "closing"/"seating": jump-cut both record layers back to CSS
      // rest, offscreen (clear the box's px and the vinyl's slide transform).
      controlsRef.current?.stop()
      inFlightRef.current = false
      openedRef.current = false
      reparkedRef.current = false
      irisRef.current = null
      clearBox()
      portholeTextureRef.current?.style.removeProperty("transform")
    }
  }, [
    phase,
    reduce,
    travels,
    rootRef,
    mediaBoxRef,
    holeProbeRef,
    portholeTextureRef,
    stageMediaRef,
  ])

  // While landed, the box follows the stage: window resizes and the Up Next
  // bottom-inset transition both change the stage frame's size, and a
  // ResizeObserver fires once per frame — the box rides the same curve with
  // direct style writes (a resize is not a choreographed moment; refs only).
  useEffect(() => {
    if (!travels) return
    if (phase !== "opening" && phase !== "active" && phase !== "upnext") return
    const root = rootRef.current
    const box = mediaBoxRef.current
    const stage = stageRef.current
    const area = stageMediaRef.current
    if (!root || !box || !stage || !area) return
    const pin = () => {
      if (!openedRef.current || inFlightRef.current) return
      const aspect =
        parseFloat(getComputedStyle(root).getPropertyValue("--media-aspect")) ||
        16 / 9
      const rect = fitRect(
        area.getBoundingClientRect(),
        root.getBoundingClientRect(),
        aspect,
      )
      box.style.left = `${rect.left}px`
      box.style.top = `${rect.top}px`
      box.style.width = `${rect.width}px`
      box.style.height = `${rect.height}px`
    }
    const observer = new ResizeObserver(pin)
    observer.observe(stage) // any viewport/inset change resizes the frame
    observer.observe(area)
    return () => observer.disconnect()
  }, [phase, travels, rootRef, mediaBoxRef, stageRef, stageMediaRef])

  // Unmount (route change / slide removal): stop whatever is running.
  useLayoutEffect(() => {
    return () => controlsRef.current?.stop()
  }, [])
}
